import { create } from 'zustand';
import { AppState, Task, Takeaway, DistillOutput } from '../types';
import { 
  loadFromDistillOutput, 
  exportToDistillOutput, 
  duplicateTask as duplicateTaskUtil,
  convertTakeawayToTask,
  revertTaskToTakeaway
} from '../utils/mappers';

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  meetingId: null,
  meetingTitle: '',
  tldr: '',
  executiveSummary: '',
  detailedSummary: '',
  tasks: [],
  takeaways: [],
  isLoading: false,
  error: null,
  
  // Original state for change detection
  originalTasks: [],
  originalTakeaways: [],
  originalTldr: '',
  originalExecutiveSummary: '',
  originalDetailedSummary: '',

  // Load data from JSON
  loadFromJson: (data: DistillOutput, meetingId?: string) => {
    try {
      const { meetingTitle, executiveSummary, tasks, takeaways } = loadFromDistillOutput(data);
      const tldr = data.meeting_brief?.tldr || '';
      const detailedSummary = data.meeting_brief?.detailed_summary || '';
      
      set({
        meetingId: meetingId || null,
        meetingTitle,
        tldr,
        executiveSummary,
        detailedSummary,
        tasks,
        takeaways,
        isLoading: false,
        error: null,
        // Store deep copy of original data for change detection
        originalTasks: JSON.parse(JSON.stringify(tasks)),
        originalTakeaways: JSON.parse(JSON.stringify(takeaways)),
        originalTldr: tldr,
        originalExecutiveSummary: executiveSummary,
        originalDetailedSummary: detailedSummary,
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load data',
        isLoading: false 
      });
    }
  },

  // Export current state to JSON
  exportToJson: (): DistillOutput => {
    const { meetingTitle, executiveSummary, tasks, takeaways } = get();
    return exportToDistillOutput(meetingTitle, executiveSummary, tasks, takeaways);
  },

  // Export current state to CSV
  exportToCsv: (): string => {
    const { tasks, takeaways } = get();
    
    // Unified CSV headers (all columns)
    const csvHeaders = [
      'Row Type', 'Title', 'Description', 'Owner', 'Requester', 'Owner Candidates (confidence)', 
      'Due Date', 'Type', 'Priority', 'Tags', 'Confidence', 'Citation'
    ];
    
    // Convert tasks to CSV rows (populate all columns)
    const taskRows = tasks.map(task => {
      const citations = (task.evidence || []).map(e => `${e.quote} (${e.speaker} ${e.elapsed_time})`).join(' | ');
      return [
        'Task',
        `"${(task.title || '').replace(/"/g, '""')}"`,
        `"${(task.description || '').replace(/"/g, '""')}"`,
        `"${(task.owner?.name || task.owner?.originalValue || '').replace(/"/g, '""')}"`,
        `"${(task.requester?.name || task.requester?.originalValue || '').replace(/"/g, '""')}"`,
        `"${(task.ownerCandidates || '').replace(/"/g, '""')}"`,
        task.due || '',
        task.type || '',
        task.priority || '',
        `"${(task.tags || []).join(', ').replace(/"/g, '""')}"`,
        task.confidence || '',
        `"${citations.replace(/"/g, '""')}"`
      ];
    });

    // Convert takeaways to CSV rows (only populate relevant columns)
    const takeawayRows = takeaways.map(takeaway => {
      const citations = (takeaway.evidence || []).map(e => `${e.quote} (${e.speaker} ${e.elapsed_time})`).join(' | ');
      return [
        'Takeaway',
        `"${(takeaway.name || '').replace(/"/g, '""')}"`,
        `"${(takeaway.description || '').replace(/"/g, '""')}"`,
        '', // owner (empty for takeaways)
        '', // requester (empty for takeaways)
        '', // owner candidates (empty for takeaways)
        '', // due date (empty for takeaways)
        takeaway.type || '',
        '', // priority (empty for takeaways)
        '', // tags (empty for takeaways)
        takeaway.confidence || '',
        `"${citations.replace(/"/g, '""')}"`
      ];
    });

    // Combine all rows with single unified header
    const allRows = [
      csvHeaders,
      ...taskRows,
      ...takeawayRows
    ];
    
    // Convert to CSV string
    return allRows.map(row => row.join(',')).join('\n');
  },

  // Task management
  updateTask: (id: string, updates: Partial<Task>) => {
    set(state => ({
      tasks: state.tasks.map(task => 
        task.id === id ? { ...task, ...updates } : task
      )
    }));
  },

  duplicateTask: (id: string) => {
    const state = get();
    const taskToDuplicate = state.tasks.find(task => task.id === id);
    
    if (taskToDuplicate) {
      const duplicatedTask = {
        ...duplicateTaskUtil(taskToDuplicate),
        origin: 'copy' as const // Mark origin as copy
      };
      set(state => ({
        tasks: [...state.tasks, duplicatedTask]
      }));
    }
  },

  addNewTask: (taskData: Partial<Task>) => {
    const state = get();
    const newTask: Task = {
      id: crypto.randomUUID(),
      meetingId: state.meetingId || undefined,
      title: taskData.title || '',
      description: taskData.description || '',
      owner: taskData.owner || null,
      requester: taskData.requester || null,
      due: taskData.due || null,
      type: taskData.type || '',
      priority: taskData.priority || null,
      tags: taskData.tags || [],
      status: 'Open',
      confidence: 0.8,
      evidence: [],
      origin: 'manual',
      isNew: true,
      isProcessed: false,
      ownerCandidates: '',
    };
    set(state => ({
      tasks: [...state.tasks, newTask]
    }));
  },

  deleteTask: (id: string) => {
    set(state => ({
      tasks: state.tasks.map(task => 
        task.id === id ? { ...task, isDeleted: true } : task
      )
    }));
  },

  revertTask: (id: string) => {
    const state = get();
    const taskToRevert = state.tasks.find(task => task.id === id);
    
    if (taskToRevert && taskToRevert.origin === 'converted' && taskToRevert.isNew) {
      const revertedTakeaway = revertTaskToTakeaway(taskToRevert);
      
      if (revertedTakeaway) {
        set(state => ({
          tasks: state.tasks.filter(task => task.id !== id),
          // Remove the converted flag from the takeaway to show it again
          takeaways: state.takeaways.map(t => 
            t.id === revertedTakeaway.id 
              ? { ...t, isConverted: false }
              : t
          )
        }));
      }
    }
  },

  // Takeaway management
  updateTakeaway: (id: string, updates: Partial<Takeaway>) => {
    set(state => ({
      takeaways: state.takeaways.map(takeaway => 
        takeaway.id === id ? { ...takeaway, ...updates } : takeaway
      )
    }));
  },

  deleteTakeaway: (id: string) => {
    set(state => ({
      takeaways: state.takeaways.map(takeaway => 
        takeaway.id === id ? { ...takeaway, isDeleted: true } : takeaway
      )
    }));
  },

  convertTakeawayToTask: (id: string) => {
    const state = get();
    const takeawayToConvert = state.takeaways.find(takeaway => takeaway.id === id);
    
    if (takeawayToConvert) {
      const newTask = {
        ...convertTakeawayToTask(takeawayToConvert),
        meetingId: state.meetingId || undefined, // Set meetingId from store
        origin: 'converted' as const, // Mark as converted from takeaway
        originalId: takeawayToConvert.id // Link to original takeaway ID
      };
      
      // Mark the takeaway as converted (will be soft deleted on save)
      const convertedTakeaway = {
        ...takeawayToConvert,
        isConverted: true // Flag for UI filtering
      };
      
      set(state => ({
        // Keep the takeaway but mark it as converted (will be filtered out in UI)
        takeaways: state.takeaways.map(t => t.id === id ? convertedTakeaway : t),
        tasks: [...state.tasks, newTask]
      }));
    }
  },

  undoTakeawayConversion: (_id: string) => {
    // No longer needed - undo is handled by revertTask
    // Takeaways can't be "undone" once saved
  },

  // Summary management
  updateTldr: (tldr: string) => {
    set({ tldr });
  },

  updateExecutiveSummary: (summary: string) => {
    set({ executiveSummary: summary });
  },

  updateDetailedSummary: (summary: string) => {
    set({ detailedSummary: summary });
  },

  // Change detection
  hasUnsavedChanges: () => {
    const state = get();
    
    // Check for grid changes (existing logic)
    const gridChanges = 
      JSON.stringify(state.tasks) !== JSON.stringify(state.originalTasks) ||
      JSON.stringify(state.takeaways) !== JSON.stringify(state.originalTakeaways);
    
    // Check for summary changes
    const summaryChanges = 
      state.tldr !== state.originalTldr ||
      state.executiveSummary !== state.originalExecutiveSummary ||
      state.detailedSummary !== state.originalDetailedSummary;
    
    return gridChanges || summaryChanges;
  },

  // Utility functions
  setError: (error: string | null) => {
    set({ error });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));
